import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ParticleBackground } from "@/components/ParticleBackground";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import Page from "@/components/layout/Page";
import { getProductPrice } from "@/utils/productHelpers";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";
import satpinImage from "@/assets/products/satpin-teal-board.png";

export default function Satpin() {
  const { addItem } = useCart();
  const { toast } = useToast();
  
  const productPrice = getProductPrice("Satpin Board");

  const handleAddToCart = () => {
    addItem({
      id: "satpin",
      name: "Satpin Board",
      slug: "/satpin",
      price: productPrice,
    });
    toast({
      title: "Added to cart",
      description: "Satpin Board has been added to your cart.",
    });
  };

  return (
    <>
      <SEOHead
        title="SATPIN Sensory Tools | Phonics Learning & Early Years Education | Samboards"
        description="Tactile SATPIN sensory tools combining phonics learning with sensory engagement. Perfect for early years, SEN schools & multi-sensory learning. UK handmade."
        canonical="/satpin"
        keywords="SATPIN phonics, sensory phonics tools, early years learning, tactile phonics, SEN education resources, multi-sensory learning, letter recognition tools, SEND schools, handmade UK"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Shop", url: "/shop" },
          { name: "SATPIN", url: "/satpin" }
        ]}
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground className="absolute inset-0 -z-10" />

        <Page>
          <section className="pb-24">
            <div className="max-w-4xl mx-auto">
              <h1 className="font-heading text-[40px] sm:text-[48px] lg:text-[56px] leading-[1.05] page-title-gradient mb-12">
                Satpin
              </h1>

              <div className="space-y-12">
                {/* Image and What difference section - side by side */}
                <div className="grid lg:grid-cols-2 gap-8 items-start">
                  {/* Product Image */}
                  <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                    <img
                      src={satpinImage}
                      alt="Satpin sensory board with textured letters S A T P I N"
                      className="w-full h-auto rounded-2xl"
                    />
                  </div>

                  {/* What difference can the Satpin make? */}
                  <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                    <h2 className="text-2xl sm:text-3xl font-heading bg-gradient-to-r from-emerald-200 via-teal-200 to-sky-300 bg-clip-text text-transparent mb-6">
                      What difference can the Satpin make?
                    </h2>
                    <p className="text-base text-slate-200/80 leading-relaxed">
                      For children and adults at the very beginning of learning their reading and writing skills, adding texture can make the experience more interesting and fun. When you move your fingers around the shaped slots each texture will give you a different feeling. If you like the feeling you are more likely to do it again. Repeating and reinforcing the movement in your memory and your muscle memory. If you are a more active learner, the action of movement while looking and writing the shapes can help you remember and make easier the task of writing. It can be used hand over hand or using a tool to give you the sound and vibrations as you move around.
                    </p>
                  </div>
                </div>

                {/* How different textures work */}
                <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                  <h2 className="text-2xl sm:text-3xl font-heading bg-gradient-to-r from-emerald-200 via-teal-200 to-sky-300 bg-clip-text text-transparent mb-6">
                    How different textures work with your sense of touch
                  </h2>
                  <p className="text-base text-slate-200/80 leading-relaxed">
                    S A T P I N are the first letters most children learn to read and write with. You can makeover 100 words with just 6 letters. The skills used to follow the letter shapes that are written can also be used on the whole alphabet.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="w-full py-20 relative z-10">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
              <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-12 text-center shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                <h2 className="mb-6 text-3xl sm:text-4xl font-heading bg-gradient-to-r from-emerald-200 via-teal-200 to-sky-300 bg-clip-text text-transparent">
                  Ready to try Satpin?
                </h2>
                <p className="mb-8 text-base text-slate-300/70 max-w-2xl mx-auto">
                  Get in touch to learn more about our products and how they can support early learning and sensory development.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button asChild size="lg" className="bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 text-slate-950 font-semibold shadow-[0_18px_55px_rgba(16,185,129,0.45)] hover:shadow-[0_22px_70px_rgba(16,185,129,0.6)] hover:translate-y-[-1px] transition-transform">
                    <Link to="/checkout?product=Satpin%20Board">Buy now - {productPrice}</Link>
                  </Button>
                  <Button onClick={handleAddToCart} size="lg" variant="outline" className="border-2 border-emerald-300/50 text-emerald-300 hover:bg-emerald-300/10">
                    Add to cart
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-2 border-emerald-300/50 text-emerald-300 hover:bg-emerald-300/10">
                    <Link to="/contact">Contact us</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </Page>
      </div>
    </>
  );
}
